import { auth } from "@/features/auth/utils/firebase";
import { unauthenticateUser } from "@gxl/web-auth";

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// Dev-only auth bypass (see AuthProvider): requests go out unauthenticated so
// the UI can be iterated against mocked or local endpoints.
const AUTH_BYPASS = import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS === "true";

const bearerHeaders = async (forceRefresh: boolean): Promise<HeadersInit> => {
  const user = auth.currentUser;
  if (!user) {
    if (AUTH_BYPASS) return {};
    throw new ApiError("Not signed in", 401);
  }
  const token = await user.getIdToken(forceRefresh);
  return { Authorization: `Bearer ${token}` };
};

let signingOutOnUnauthorized = false;

const signOutOnUnauthorized = () => {
  // The bypass user has no Firebase session to clear; signing out would reload the page in a loop.
  if (AUTH_BYPASS || signingOutOnUnauthorized) return;
  signingOutOnUnauthorized = true;
  void unauthenticateUser(auth);
};

export const apiFetch = async (
  path: string,
  init?: RequestInit,
): Promise<Response> => {
  const url = path.startsWith("/api/") ? path : `/api${path}`;
  const first = await bearerHeaders(false);
  let response = await fetch(url, {
    ...init,
    headers: { ...init?.headers, ...first },
  });
  if (response.status === 401) {
    try {
      const second = await bearerHeaders(true);
      response = await fetch(url, {
        ...init,
        headers: { ...init?.headers, ...second },
      });
    } catch {
      signOutOnUnauthorized();
      return response;
    }
  }
  if (response.status === 401) {
    signOutOnUnauthorized();
  }
  return response;
};

export const parseErrorDetail = async (response: Response): Promise<string> => {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as { detail: unknown }).detail;
      if (typeof detail === "string") return detail;
    }
  } catch {
    /* ignore */
  }
  return `Request failed (${response.status})`;
};
